import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import useHelps from "../hooks/useHelps";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import SettingsIcon from "@material-ui/icons/Settings";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import ViewKanban from "@mui/icons-material/ViewKanban";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Schedule from "@material-ui/icons/Schedule";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import StorefrontIcon from "@material-ui/icons/Storefront";
import MemoryIcon from "@material-ui/icons/Memory";
import LayersIcon from '@material-ui/icons/Layers';
import CakeIcon from "@material-ui/icons/Cake";
import PeopleIcon from "@material-ui/icons/People";
import ListIcon from "@material-ui/icons/ListAlt";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import ForumIcon from "@material-ui/icons/Forum";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined"; // [ANTIGRAVITY]
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import BusinessIcon from "@material-ui/icons/Business";
import SystemUpdateAltIcon from "@material-ui/icons/SystemUpdateAlt";
import SpeedIcon from "@material-ui/icons/Speed";
import BuildIcon from "@material-ui/icons/Build";
import CallIcon from "@material-ui/icons/Call";
import ChangesLog from "../components/ChangesLog";
import { changes } from "../components/ChangesLog/changes";
import {
  AllInclusive,
  AttachFile,
  Dashboard,
  Description,
  DeviceHubOutlined,
  GridOn,
  PhonelinkSetup,
  Assessment,
  Assignment,
} from "@material-ui/icons";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";

import { Can } from "../components/Can";

import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import { i18n } from "../translate/i18n";
import { Campaign, ShapeLine, Webhook } from "@mui/icons-material";

import useCompanySettings from "../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => ({
  listItem: (props) => ({
    height: "44px !important",
    minHeight: "44px !important",
    width: "auto !important", 
    minWidth: "auto !important",
    maxWidth: "none !important",
    margin: props.collapsed ? "8px 0 !important" : "8px 16px",
    padding: props.collapsed ? "0 !important" : undefined,
    paddingLeft: props.collapsed ? "0 !important" : "16px",
    paddingRight: props.collapsed ? "0 !important" : undefined,
    display: "flex",
    justifyContent: props.collapsed ? "center !important" : undefined,
    alignItems: "center !important",
    position: "relative",
    // Transition
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    
    // Transparent container
    backgroundColor: "transparent !important",
    boxShadow: "none !important",
    
    // Visual shape via ::before
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      left: props.collapsed ? "50%" : 0,
      right: props.collapsed ? "auto" : 0,
      width: props.collapsed ? "44px" : "100%",
      transform: props.collapsed ? "translateX(-50%)" : "none",
      borderRadius: props.collapsed ? "50%" : "22px",
      backgroundColor: theme.palette.type === "light" ? "#F8FAFC" : "#3A3F47",
      boxShadow: theme.palette.type === "light" ? "none" : "5px 5px 10px #1d2024, -5px -5px 10px #353a40",
      border: theme.palette.type === "light" ? "1px solid #E2E8F0" : "none",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      zIndex: 0,
    },
    
    // Ensure content sits above the background
    "& > *": {
      zIndex: 1,
    },

    "& .MuiListItemIcon-root": {
      minWidth: props.collapsed ? "44px !important" : undefined,
      marginRight: props.collapsed ? "0 !important" : undefined,
      justifyContent: props.collapsed ? "center !important" : undefined,
      alignItems: props.collapsed ? "center !important" : undefined,
    },

    // Fix for TouchRipple bleeding out of rounded corners
    "& .MuiTouchRipple-root": {
      borderRadius: props.collapsed ? "50%" : "22px",
    },

    // Hover State
    "&:hover": {
      backgroundColor: "transparent !important",
      border: "none",
      boxShadow: "none !important",
      transform: "translateY(-1px)",

      "&::before": {
        boxShadow: `0 0 10px ${theme.palette.primary.main}99, inset 2px 2px 5px rgba(0,0,0,0.5)`,
        border: `1px solid ${theme.palette.primary.main}80`,
      },

      "& $listItemText": {
        color: theme.palette.primary.main,
        textShadow: `0 0 8px ${theme.palette.primary.main}99`,
      },
      "& $listItemIcon": {
        color: theme.palette.primary.main,
      },
      "& .MuiListItemIcon-root": {
        color: theme.palette.primary.main,
      },

      "&::after": {
        display: "none",
      },
    },
  }),

  listItemText: {
    fontSize: "0.78rem", // Reduced size
    color: theme.palette.type === "light" ? "#334155" : "#e2e8f0",
    transition: "color 0.2s ease",
    fontWeight: 400,
    fontFamily: "'Inter', 'Roboto', sans-serif !important",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "& .MuiTypography-root": {
      fontFamily: "'Inter', 'Roboto', sans-serif !important",
      fontWeight: 400,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }
  },

  avatarActive: {
    backgroundColor: "transparent",
  },

  avatarHover: {
    backgroundColor: "transparent",
  },

  // Icon Style
  iconHoverActive: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 34,
    width: 34,
    backgroundColor: "transparent",
    color: theme.palette.type === "light" ? "#64748B" : "#94a3b8",
    transition: "all 0.2s ease",
    marginRight: 0, // Removed double margin (handled by ListItemIcon)
    borderRadius: "50%",

    // Active State
    "&.active": {
      color: theme.palette.primary.main, // Tiny color accent
      filter: "none",
    },

    "&:hover": {
      color: "#EEEEEE",
      filter: "none",
    },

    "& .MuiSvgIcon-root": {
      fontSize: "1.3rem",
      transition: "all 0.2s ease",
    },
  },

  // Badge melhorado
  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#ef4444",
      color: "#fff",
      boxShadow: "0 0 0 2px #fff",
    }
  },

  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.7,
    }
  },

  // Melhorias para submenus mantendo estrutura original
  submenuContainer: {
    backgroundColor: "transparent",
    marginTop: "4px",
    marginBottom: "4px",
    "& .MuiListItem-root:not($parentMenuItem)": {
      height: "38px !important",
      minHeight: "38px !important",
      margin: "4px 24px !important",
      width: "auto !important",
      
      "&::before": {
        backgroundColor: "rgba(0, 0, 0, 0.15) !important",
        boxShadow: "none !important",
        border: "none !important",
        borderRadius: "8px !important",
        borderLeft: `3px solid ${theme.palette.primary.main}4D !important`,
      },

      "& .MuiTouchRipple-root": {
        borderRadius: "8px !important",
      },

      "&:hover": {
        "&::before": {
          backgroundColor: `${theme.palette.primary.main}0D !important`,
          borderLeft: `3px solid ${theme.palette.primary.main} !important`,
          boxShadow: `0 0 10px ${theme.palette.primary.main}4D !important`,
        },
        "& $listItemText, & .MuiTypography-root, & .MuiSvgIcon-root, & .MuiListItemIcon-root": {
          color: `${theme.palette.primary.main} !important`,
          textShadow: `0 0 8px ${theme.palette.primary.main}99 !important`,
        }
      },
      
      "&$activeItem": {
        "&::before": {
          backgroundColor: `${theme.palette.primary.main}1A !important`,
          borderLeft: `3px solid ${theme.palette.primary.main} !important`,
          boxShadow: `0 0 12px ${theme.palette.primary.main}80 !important`,
        }
      }
    }
  },

  // Tooltip melhorado
  customTooltip: {
    backgroundColor: "#111418", // Dark for neon effect
    color: theme.palette.primary.main,
    fontSize: "0.7rem",
    fontWeight: 400,
    borderRadius: "8px",
    border: `1px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 10px ${theme.palette.primary.main}99`,
    "& .MuiTooltip-arrow": {
      color: theme.palette.primary.main,
    }
  },
  customDivider: {
    borderTop: "1px solid #ffffff",
    boxShadow: "0 -1px 4px rgba(255, 255, 255, 0.4)",
    margin: "12px 16px",
    backgroundColor: "transparent",
  },


  // Versão com destaque White Neon
  versionContainer: {
    textAlign: "center",
    padding: "10px",
    color: "#ffffff", // Intense White
    fontSize: "12px",
    fontWeight: "bold",
    textShadow: "0 0 5px rgba(255, 255, 255, 0.8)", // White Glow
    borderTop: "1px solid #ffffff", // White Line
    boxShadow: "0 -1px 4px rgba(255, 255, 255, 0.4)", // Glow on line
    marginTop: "auto",
  },

  // Seções de administración (Headers) - Centered + INTENSE White Neon Lines (Pseudo-elements)
  subHeader: {
    color: `${theme.palette.primary.main} !important`,
    textAlign: "center",
    fontSize: "0.80rem",
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    textShadow: `0 0 5px ${alpha(theme.palette.primary.main, 0.4)}`,
    margin: "10px 0",
    padding: "0", // Reset padding to avoid offset
    position: "relative", // For pseudo-elements
    lineHeight: "1.5",
    backgroundColor: "transparent",

    // Top Line (Hidden for Neumorphism)
    "&::before": {
      display: "none",
    },

    // Bottom Line (Hidden for Neumorphism)
    "&::after": {
      display: "none",
    },
  },

  // Efeitos suaves para expand/collapse
  expandIcon: {
    transition: "transform 0.3s ease",
    color: theme.palette.primary.main, // Usa cor do tema
    "&.expanded": {
      transform: "rotate(180deg)",
    }
  },

  // Menu container com melhorias sutis
  menuContainer: {
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.mode === "light"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.1)",
      borderRadius: "3px",
      "&:hover": {
        background: theme.mode === "light"
          ? "rgba(0, 0, 0, 0.2)"
          : "rgba(255, 255, 255, 0.2)",
      }
    },
  },

  // NEUMORPHIC ACTIVE STATE
  activeItem: {
    backgroundColor: "transparent !important",
    border: "none",
    boxShadow: "none !important",
    transform: "translateY(1px)", 
    position: "relative",
    transition: "all 0.2s ease-in-out",

    // Neon effect on the ::before shape
    "&::before": {
      backgroundColor: theme.palette.type === "light" ? "#EFF6FF !important" : "#1A1D21 !important",
      boxShadow: theme.palette.type === "light" ? "0 1px 4px rgba(0,0,0,0.06) !important" : `0 0 12px ${theme.palette.primary.main}, inset 2px 2px 5px rgba(0,0,0,0.5) !important`,
      border: `1px solid ${theme.palette.primary.main} !important`,
    },

    "& $listItemText": {
      color: `${theme.palette.primary.main} !important`,
      fontWeight: 600,
      textShadow: theme.palette.type === "light" ? "none" : `0 0 8px ${theme.palette.primary.main}80`,
    },

    "& $iconHoverActive": {
      color: `${theme.palette.primary.main} !important`,
      filter: `drop-shadow(0 0 5px ${theme.palette.primary.main}80)`,
    },

    "& .MuiListItemIcon-root": {
      color: `${theme.palette.primary.main} !important`,
    },
    "&::after": {
      display: "none",
    }
  },

  parentMenuItem: {
    position: "relative",
    transition: "all 0.3s ease-in-out",

    "& .MuiTypography-root": {
      fontWeight: 400,
      letterSpacing: "0.5px",
      color: theme.palette.type === "light" ? "#1E293B !important" : "#EEEEEE !important",
      textShadow: "none",
    },
    
    "& $iconHoverActive": {
      color: theme.palette.primary.main,
      filter: "none",
    },

    "&:hover": {
      "&::before": {
        boxShadow: "7px 7px 15px rgba(0,0,0,0.5), -7px -7px 15px rgba(255,255,255,0.04)", // Pop more
      }
    }
  },

  // Fix spacing between icon and text (MUI default is 56px)
  listItemIcon: {
    minWidth: "auto",
    marginRight: "6px", // Reduced to single tight margin
  },
}));

function ListItemLink(props) {
  const { icon, primary, to, tooltip, showBadge, className } = props;
  const classes = useStyles({ collapsed: !!tooltip });
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive = activeMenu === to || (location.pathname === to && activeMenu !== "UPDATES");

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const ConditionalTooltip = ({ children }) => (
    <Tooltip classes={{ tooltip: classes.customTooltip }} title={primary} placement="right">
      {children}
    </Tooltip>
  );

  return (
    <ConditionalTooltip>
      <li>
        <ListItem
          button
          component={renderLink}
          className={`${classes.listItem} ${isActive ? classes.activeItem : ""} ${className || ""}`}
          style={{
            justifyContent: tooltip ? "center" : "flex-start",
            paddingLeft: 0,
            paddingRight: 16
          }}
        >
          {icon ? (
            <ListItemIcon className={classes.listItemIcon} style={{
              minWidth: 40,
              marginRight: tooltip ? 0 : 16,
              justifyContent: "center"
            }}>
              {showBadge ? (
                <Badge
                  badgeContent="!"
                  color="error"
                  overlap="circular"
                  className={classes.badge}
                >
                  <Avatar
                    className={`${classes.iconHoverActive} ${isActive ? "active" : ""
                      }`}
                  >
                    {icon}
                  </Avatar>
                </Badge>
              ) : (
                <Avatar
                  className={`${classes.iconHoverActive} ${isActive ? "active" : ""
                    }`}
                >
                  {icon}
                </Avatar>
              )}
            </ListItemIcon>
          ) : null}
          <ListItemText
            primary={
              <Typography className={classes.listItemText}>
                {primary}
              </Typography>
            }
            style={tooltip ? { display: "none" } : {}}
          />
        </ListItem>
      </li>
    </ConditionalTooltip>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = ({ collapsed, drawerClose }) => {
  const theme = useTheme();
  const classes = useStyles({ collapsed });
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);

  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openCampaignSubmenu, setOpenCampaignSubmenu] = useState(false);
  const [openDashboardSubmenu, setOpenDashboardSubmenu] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showWavoipCall, setShowWavoipCall] = useState(false);

  // novas features
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);

  // [ANTIGRAVITY] New State for Appointments Submenu
  const [openAppointmentsSubmenu, setOpenAppointmentsSubmenu] = useState(false);

  // [ANTIGRAVITY] New State for Communications Submenu
  const [openCommunicationsSubmenu, setOpenCommunicationsSubmenu] = useState(false);
  const [communicationsHover, setCommunicationsHover] = useState(false);
  const [openListsSubmenu, setOpenListsSubmenu] = useState(false);
  const [listsHover, setListsHover] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [version, setVersion] = useState(false);
  const [managementHover, setManagementHover] = useState(false);
  const [campaignHover, setCampaignHover] = useState(false);
  const { list } = useHelps(); // INSERIR
  const [hasHelps, setHasHelps] = useState(false);

  const [openFlowSubmenu, setOpenFlowSubmenu] = useState(false);
  const [flowHover, setFlowHover] = useState(false);
  const [openToolsSubmenu, setOpenToolsSubmenu] = useState(false);
  const [toolsHover, setToolsHover] = useState(false);
  const [openPublishSubmenu, setOpenPublishSubmenu] = useState(false);
  const [publishHover, setPublishHover] = useState(false);
  const [openAdminSubmenu, setOpenAdminSubmenu] = useState(false);
  const [adminHover, setAdminHover] = useState(false);

  const { get: getSetting } = useCompanySettings();

  const [showWallets, setShowWallets] = useState(false);
  const [changesLogOpen, setChangesLogOpen] = useState(false);
  const [appointmentsHover, setAppointmentsHover] = useState(false);

  const isFlowbuilderRouteActive =
    location.pathname.startsWith("/phrase-lists") ||
    location.pathname.startsWith("/flowbuilder");

  useEffect(() => {
    // INSERIR ESSE EFFECT INTEIRO
    async function checkHelps() {
      if (!user?.companyId) return;
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!user?.companyId) return;
      try {
        const setting = await getSetting(
          {
            "column": "DirectTicketsToWallets"
          }
        );

        if (setting && setting.DirectTicketsToWallets !== undefined) {
          setShowWallets(setting.DirectTicketsToWallets === 'true' || setting.DirectTicketsToWallets === true);
        } else {
          setShowWallets(false);
        }

      } catch (err) {
        toastError(err);
      }
    }

    fetchSettings();
  }, [setShowWallets]);

  const isManagementActive =
    location.pathname === "/" ||
    location.pathname.startsWith("/reports") ||
    location.pathname.startsWith("/moments");

  const isCampaignRouteActive =
    location.pathname === "/campaigns" ||
    location.pathname.startsWith("/contact-lists") ||
    location.pathname.startsWith("/campaigns-config");

  const isCommunicationsActive =
    location.pathname.startsWith("/tickets") ||
    location.pathname.startsWith("/emails") ||
    location.pathname.startsWith("/chats");

  const isListsActive =
    location.pathname.startsWith("/contacts") ||
    location.pathname.startsWith("/companies") ||
    location.pathname.startsWith("/all-contacts") ||
    location.pathname.startsWith("/files") ||
    location.pathname.startsWith("/products");

  useEffect(() => {
    if (changesLogOpen) {
      setActiveMenu("UPDATES");
      return;
    }
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu, changesLogOpen]);

  const { getPlanCompany } = usePlans();

  const { getVersion } = useVersion();

  useEffect(() => {
    async function fetchVersion() {
      if (!user?.companyId) return;
      const _version = await getVersion();
      setVersion(_version.version);
    }
    fetchVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      if (!user?.companyId) return;
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      setShowCampaigns(planConfigs.plan?.useCampaigns || false);
      setShowSchedules(planConfigs.plan?.useSchedules || false);
      setShowInternalChat(planConfigs.plan?.useInternalChat || false);
      setShowExternalApi(planConfigs.plan?.useExternalApi || false);
      setShowKanban(planConfigs.plan?.useKanban || false);
      setShowOpenAi(planConfigs.plan?.useOpenAi || false);
      setShowIntegrations(planConfigs.plan?.useIntegrations || false);
      setShowWavoipCall(planConfigs.plan?.wavoip || false);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id && socket && typeof socket.on === 'function') {
      const companyId = user.companyId;

      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      const eventName = `company-${companyId}-chat`;
      socket.on(eventName, onCompanyChatMainListItems);

      return () => {
        if (socket && typeof socket.off === 'function') {
          socket.off(eventName, onCompanyChatMainListItems);
        }
      };
    }
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  // useEffect(() => {
  //   if (localStorage.getItem("cshow")) {
  //     setShowCampaigns(true);
  //   }
  // }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    if (!user?.companyId) return;
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div onClick={drawerClose}>
      <Can
        role={
          (user.profile === "user" && user.showDashboard === "enabled") ||
            user.allowRealTime === "enabled"
            ? "admin"
            : user.profile
        }
        perform={"drawer-admin-items:view"}
        yes={() => (
          <>
            <Tooltip classes={{ tooltip: classes.customTooltip }}
              title={collapsed ? i18n.t("mainDrawer.listItems.management") : ""}
              placement="right"
            >
              <ListItem
                dense
                button
                onClick={() => setOpenDashboardSubmenu((prev) => !prev)}
                onMouseEnter={() => setManagementHover(true)}
                onMouseLeave={() => setManagementHover(false)}
                className={`${classes.listItem} ${classes.parentMenuItem}`}
                style={{
                  justifyContent: collapsed ? "center" : "flex-start",
                  paddingLeft: 0,
                  paddingRight: 16
                }}
              >
                <ListItemIcon style={{
                  minWidth: 40,
                  marginRight: collapsed ? 0 : 16,
                  justifyContent: "center"
                }}>
                  <Avatar
                    className={`${classes.iconHoverActive} ${isManagementActive || managementHover ? "active" : ""
                      }`}
                  >
                    <Dashboard />
                  </Avatar>
                </ListItemIcon>
                <ListItemText style={{ display: collapsed ? "none" : "block" }}
                  primary={
                    <Typography className={classes.listItemText}>
                      {i18n.t("mainDrawer.listItems.management")}
                    </Typography>
                  }
                  style={collapsed ? { display: "none" } : {}}
                />
                {collapsed ? null : openDashboardSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
            </Tooltip>
            <Collapse
              in={openDashboardSubmenu}
              timeout="auto"
              unmountOnExit
              className={classes.submenuContainer}
            >
              <Can
                role={
                  user.profile === "user" && user.showDashboard === "enabled"
                    ? "admin"
                    : user.profile
                }
                perform={"drawer-admin-items:view"}
                yes={() => (
                  <>
                    <ListItemLink
                      small
                      to="/"
                      primary={i18n.t("mainDrawer.listItems.dashboard")}
                      icon={<DashboardOutlinedIcon />}
                      tooltip={collapsed}
                    />
                    <ListItemLink
                      small
                      to="/webhook-logs"
                      primary={i18n.t("mainDrawer.listItems.audit")}
                      icon={<Assignment />}
                      tooltip={collapsed}
                    />
                    <ListItemLink
                      small
                      to="/reports"
                      primary={i18n.t("mainDrawer.listItems.reports")}
                      icon={<Description />}
                      tooltip={collapsed}
                    />
                  </>
                )}
              />
              <Can
                role={
                  user.profile === "user" && user.allowRealTime === "enabled"
                    ? "admin"
                    : user.profile
                }
                perform={"drawer-admin-items:view"}
                yes={() => (
                  <ListItemLink
                    to="/moments"
                    primary={i18n.t("mainDrawer.listItems.chatsTempoReal")}
                    icon={<GridOn />}
                    tooltip={collapsed}
                  />
                )}
              />
              <Can
                role={user.profile}
                perform={"drawer-admin-items:view"}
                yes={() => (
                  <ListItemLink
                    to="/bot-monitor"
                    primary={i18n.t("botMonitor.title")}
                    icon={<Assessment />}
                    tooltip={collapsed}
                  />
                )}
              />

              {user.profile === "admin" && showWallets && (
                <>
                  <ListItemLink
                    to="/wallets"
                    primary={i18n.t("mainDrawer.listItems.wallets")}
                    icon={<AccountBalanceWalletIcon />}
                    tooltip={collapsed}
                  />
                </>
              )}
            </Collapse>
          </>
        )}
      />
      <Tooltip classes={{ tooltip: classes.customTooltip }}
        title={collapsed ? i18n.t("mainDrawer.listItems.communications") : ""}
        placement="right"
      >
        <ListItem
          dense
          button
          onClick={() => setOpenCommunicationsSubmenu((prev) => !prev)}
          onMouseEnter={() => setCommunicationsHover(true)}
          onMouseLeave={() => setCommunicationsHover(false)}
          className={`${classes.listItem} ${classes.parentMenuItem}`}
          style={{
            justifyContent: collapsed ? "center" : "flex-start",
            paddingLeft: 0,
            paddingRight: 16
          }}
        >
          <ListItemIcon style={{
            minWidth: 40,
            marginRight: collapsed ? 0 : 16,
            justifyContent: "center"
          }}>
            <Avatar
              className={`${classes.iconHoverActive} ${isCommunicationsActive || communicationsHover ? "active" : ""
                }`}
            >
              <ForumIcon />
            </Avatar>
          </ListItemIcon>
          <ListItemText style={{ display: collapsed ? "none" : "block" }}
            primary={
              <Typography className={classes.listItemText}>
                {i18n.exists("mainDrawer.listItems.communications") ? i18n.t("mainDrawer.listItems.communications") : "Comunicaciones"}
              </Typography>
            }
            style={collapsed ? { display: "none" } : {}}
          />
          {collapsed ? null : openCommunicationsSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItem>
      </Tooltip>
      <Collapse
        in={openCommunicationsSubmenu}
        timeout="auto"
        unmountOnExit
        className={classes.submenuContainer}
      >
        <List dense component="div" disablePadding>
          <ListItemLink
            to="/tickets"
            primary={i18n.exists("mainDrawer.listItems.tickets") ? i18n.t("mainDrawer.listItems.tickets") : "Inbox de chat"}
            icon={<WhatsAppIcon />}
            tooltip={collapsed}
          />
          <ListItemLink
            to="/emails"
            primary={i18n.exists("mainDrawer.listItems.emails") ? i18n.t("mainDrawer.listItems.emails") : "Inbox de correo"}
            icon={<EmailOutlinedIcon />}
            tooltip={collapsed}
          />
          {showInternalChat && (
            <ListItemLink
              to="/chats"
              primary={i18n.exists("mainDrawer.listItems.chats") ? i18n.t("mainDrawer.listItems.chats") : "Chats de equipo"}
              icon={
                <Badge color="secondary" variant="dot" invisible={invisible}>
                  <ForumIcon />
                </Badge>
              }
              tooltip={collapsed}
            />
          )}
{showWavoipCall && (
        <ListItemLink
          to="/call-historicals"
          primary={i18n.t("mainDrawer.listItems.callHistorical")}
          icon={<CallIcon />}
          tooltip={collapsed}
        />
      )}
<ListItemLink
        to="/quick-messages"
        primary={i18n.t("mainDrawer.listItems.quickMessages")}
        icon={<FlashOnIcon />}
        tooltip={collapsed}
      />
{showKanban && (
        <>
          <ListItemLink
            to="/kanban"
            primary={i18n.t("mainDrawer.listItems.kanban")}
            icon={<ViewKanban />}
            tooltip={collapsed}
          />
        </>
      )}
        </List>
      </Collapse>

      {showWavoipCall && (
        <ListItemLink
          to="/call-historicals"
          primary={i18n.t("mainDrawer.listItems.callHistorical")}
          icon={<HistoryToggleOffIcon />}
          tooltip={collapsed}
        />
      )}

      

      

      <Tooltip classes={{ tooltip: classes.customTooltip }}
        title={i18n.exists("mainDrawer.listItems.lists") ? i18n.t("mainDrawer.listItems.lists") : "Listas"}
        placement="right"
      >
        <ListItem
          dense
          button
          onClick={() => setOpenListsSubmenu((prev) => !prev)}
          onMouseEnter={() => setListsHover(true)}
          onMouseLeave={() => setListsHover(false)}
          className={`${classes.listItem} ${classes.parentMenuItem}`}
          style={{
            justifyContent: collapsed ? "center" : "flex-start",
            paddingLeft: 0,
            paddingRight: 16
          }}
        >
          <ListItemIcon style={{
            minWidth: 40,
            marginRight: collapsed ? 0 : 16,
            justifyContent: "center"
          }}>
            <Avatar
              className={`${classes.iconHoverActive} ${isListsActive || listsHover ? "active" : ""}`}
            >
              <ListIcon />
            </Avatar>
          </ListItemIcon>
          <ListItemText style={{ display: collapsed ? "none" : "block" }}
            primary={
              <Typography className={classes.listItemText}>
                {i18n.exists("mainDrawer.listItems.lists") ? i18n.t("mainDrawer.listItems.lists") : "Listas"}
              </Typography>
            }
            style={collapsed ? { display: "none" } : {}}
          />
          {collapsed ? null : openListsSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItem>
      </Tooltip>
      <Collapse
        in={openListsSubmenu}
        timeout="auto"
        unmountOnExit
        className={classes.submenuContainer}
      >
        <List dense component="div" disablePadding>
          {user.showContacts === "enabled" && (
            <ListItemLink
              to="/contacts"
              primary={i18n.t("mainDrawer.listItems.contacts")}
              icon={<ContactPhoneOutlinedIcon />}
              tooltip={collapsed}
            />
          )}
          <ListItemLink
            to="/tags"
            primary={i18n.t("mainDrawer.listItems.tags")}
            icon={<LocalOfferIcon />}
            tooltip={collapsed}
          />
          <ListItemLink
            to="/contact-companies"
            primary={i18n.exists("mainDrawer.listItems.contactCompanies") ? i18n.t("mainDrawer.listItems.contactCompanies") : "Compañías"}
            icon={<BusinessIcon />}
            tooltip={collapsed}
          />

          <ListItemLink
            to="/all-contacts"
            primary={i18n.exists("mainDrawer.listItems.allContacts") ? i18n.t("mainDrawer.listItems.allContacts") : "Contactos y Empresas"}
            icon={<PeopleIcon />}
            tooltip={collapsed}
          />
          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <ListItemLink
                to="/files"
                primary={i18n.t("mainDrawer.listItems.files")}
                icon={<AttachFile />}
                tooltip={collapsed}
              />
            )}
          />
          <ListItemLink
            to="/products"
            primary={i18n.exists("mainDrawer.listItems.products") ? i18n.t("mainDrawer.listItems.products") : "Productos"}
            icon={<StorefrontIcon />}
            tooltip={collapsed}
          />
        </List>
      </Collapse>

      {showSchedules && (
        <>
          <Tooltip classes={{ tooltip: classes.customTooltip }}
            title={collapsed ? i18n.t("mainDrawer.listItems.agendamiento") : ""}
            placement="right"
          >
            <ListItem
              dense
              button
              onClick={() => setOpenAppointmentsSubmenu(!openAppointmentsSubmenu)}
              onMouseEnter={() => setAppointmentsHover(true)}
              onMouseLeave={() => setAppointmentsHover(false)}
              className={`${classes.listItem} ${classes.parentMenuItem}`}
              style={{
                justifyContent: collapsed ? "center" : "flex-start",
                paddingLeft: 0,
                paddingRight: 16
              }}
            >
              <ListItemIcon style={{ minWidth: 40, marginRight: collapsed ? 0 : 16, justifyContent: "center" }}>
                <Avatar className={`${classes.iconHoverActive} ${openAppointmentsSubmenu || appointmentsHover ? "active" : ""}`}>
                  <EventAvailableIcon />
                </Avatar>
              </ListItemIcon>
              <ListItemText style={{ display: collapsed ? "none" : "block" }}
                primary={
                  <Typography className={classes.listItemText}>
                    {i18n.t("mainDrawer.listItems.agendamiento")}
                  </Typography>
                }
                style={collapsed ? { display: "none" } : {}}
              />
              {collapsed ? null : openAppointmentsSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItem>
          </Tooltip>
          <Collapse
            in={openAppointmentsSubmenu}
            timeout="auto"
            unmountOnExit
            className={classes.submenuContainer}
          >
            <List dense component="div" disablePadding>
              <ListItemLink
                to="/appointments"
                primary={i18n.t("mainDrawer.listItems.appointments") || "Agenda de Citas"}
                icon={<EventAvailableIcon />}
                tooltip={collapsed}
              />
              <ListItemLink
                to="/services"
                primary={i18n.t("mainDrawer.listItems.services")}
                icon={<ListIcon />}
                tooltip={collapsed}
              />
              <ListItemLink
                to="/professionals"
                primary={i18n.t("mainDrawer.listItems.professionals")}
                icon={<PeopleIcon />}
                tooltip={collapsed}
              />
                {showSchedules && (
                  <ListItemLink
                    to="/schedules"
                    primary={i18n.t("mainDrawer.listItems.schedules")}
                    icon={<Schedule />}
                    tooltip={collapsed}
                  />
                )}
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/birthday-settings"
                    primary={i18n.t("mainDrawer.listItems.birthdaySettings")}
                    icon={<CakeIcon />}
                    tooltip={collapsed}
                  />
                )}
              />
            </List>
          </Collapse>
        </>
      )}

      

      {/* Internal Chat was moved to Comunicaciones submenu */}

      {/* 
      <ListItemLink
        to="/todolist"
        primary={i18n.t("ToDoList")}
        icon={<EventAvailableIcon />}
      /> 
      */}


      {user?.showCampaign === "enabled" && showCampaigns && (
        <>
          <Tooltip classes={{ tooltip: classes.customTooltip }}
            title={collapsed ? i18n.t("mainDrawer.listItems.campaigns") : ""}
            placement="right"
          >
            <ListItem
              dense
              button
              onClick={() => setOpenCampaignSubmenu((prev) => !prev)}
              onMouseEnter={() => setCampaignHover(true)}
              onMouseLeave={() => setCampaignHover(false)}
              className={`${classes.listItem} ${classes.parentMenuItem}`}
              style={{
                justifyContent: collapsed ? "center" : "flex-start",
                paddingLeft: 0,
                paddingRight: 16
              }}
            >
              <ListItemIcon style={{
                minWidth: 40,
                marginRight: collapsed ? 0 : 16,
                justifyContent: "center"
              }}>
                <Avatar
                  className={`${classes.iconHoverActive} ${isCampaignRouteActive || campaignHover ? "active" : ""}`}
                >
                  <EventAvailableIcon />
                </Avatar>
              </ListItemIcon>
              <ListItemText style={{ display: collapsed ? "none" : "block" }}
                primary={
                  <Typography className={classes.listItemText}>
                    {i18n.t("mainDrawer.listItems.campaigns")}
                  </Typography>
                }
                style={collapsed ? { display: "none" } : {}}
              />
              {collapsed ? null : openCampaignSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItem>
          </Tooltip>
          <Collapse
            in={openCampaignSubmenu}
            timeout="auto"
            unmountOnExit
            className={classes.submenuContainer}
          >
            <List dense component="div" disablePadding>
              <ListItemLink
                to="/campaigns"
                primary={i18n.t("campaigns.subMenus.list")}
                icon={<ListIcon />}
                tooltip={collapsed}
              />
              <ListItemLink
                to="/contact-lists"
                primary={i18n.t("campaigns.subMenus.listContacts")}
                icon={<PeopleIcon />}
                tooltip={collapsed}
              />
              <ListItemLink
                to="/campaigns-config"
                primary={i18n.t("campaigns.subMenus.settings")}
                icon={<SettingsOutlinedIcon />}
                tooltip={collapsed}
              />

            </List>
          </Collapse>
        </>
      )}

      <Divider className={classes.customDivider} />
      {/* FLOWBUILDER */}
      {user.showFlow === "enabled" && (
        <>
          <Tooltip classes={{ tooltip: classes.customTooltip }}
            title={
              collapsed ? i18n.t("mainDrawer.listItems.campaigns") : ""
            }
            placement="right"
          >
            <ListItem
              dense
              button
              onClick={() => setOpenFlowSubmenu((prev) => !prev)}
              onMouseEnter={() => setFlowHover(true)}
              onMouseLeave={() => setFlowHover(false)}
              className={`${classes.listItem} ${classes.parentMenuItem}`}
              style={{
                justifyContent: collapsed ? "center" : "flex-start",
                paddingLeft: 0,
                paddingRight: 16
              }}
            >
              <ListItemIcon style={{
                minWidth: 40,
                marginRight: collapsed ? 0 : 16,
                justifyContent: "center"
              }}>
                <Avatar
                  className={`${classes.iconHoverActive} ${isFlowbuilderRouteActive || flowHover
                    ? "active"
                    : ""
                    }`}
                >
                  <Webhook />
                </Avatar>
              </ListItemIcon>
              <ListItemText style={{ display: collapsed ? "none" : "block" }}
                primary={
                  <Typography className={classes.listItemText}>
                    {i18n.t("mainDrawer.listItems.automations")}
                  </Typography>
                }
                style={collapsed ? { display: "none" } : {}}
              />
              {collapsed ? null : openFlowSubmenu ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </ListItem>
          </Tooltip>

          <Collapse
            in={openFlowSubmenu}
            timeout="auto"
            unmountOnExit
            className={classes.submenuContainer}
          >
            <List dense component="div" disablePadding>


              <ListItemLink
                to="/flowbuilders"
                primary={i18n.t("mainDrawer.listItems.flowBuilder") || "Fluxo de conversa"}
                icon={<ShapeLine />}
                tooltip={collapsed}
              />

              <ListItemLink
                to="/meta-comments"
                primary={i18n.t("mainDrawer.listItems.metaComments")}
                icon={<ForumIcon />}
                tooltip={collapsed}
              />

              {/* [Plan 928] Link a los formularios */}
              <ListItemLink
                to="/forms"
                primary={i18n.t("mainDrawer.listItems.webForms")}
                icon={<Assignment />}
                tooltip={collapsed}
              />
            {showExternalApi && (
              <>
                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/messages-api"
                      primary={i18n.t("mainDrawer.listItems.messagesAPI")}
                      icon={<CodeRoundedIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />
              </>
            )}
            {showIntegrations && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/queue-integration"
                    primary={i18n.t("mainDrawer.listItems.queueIntegration")}
                    icon={<DeviceHubOutlined />}
                    tooltip={collapsed}
                  />
                )}
              />
            )}
            {showIntegrations && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/event-webhooks"
                    primary={i18n.t("mainDrawer.listItems.webhooks")}
                    icon={<Webhook />}
                    tooltip={collapsed}
                  />
                )}
              />
            )}
            <Can
              role={
                user.profile === "user" && user.allowConnections === "enabled"
                  ? "admin"
                  : user.profile
              }
              perform={"drawer-admin-items:view"}
              yes={() => (
                <ListItemLink
                  to="/web-widget"
                  primary={i18n.t("mainDrawer.listItems.webWidget")}
                  icon={<CodeRoundedIcon />}
                  tooltip={collapsed}
                />
              )}
            />
            </List>
          </Collapse>
        </>
      )}

      <Can
        role={
          user.profile === "user" && user.allowConnections === "enabled"
            ? "admin"
            : user.profile
        }
        perform="dashboard:view"
        yes={() => (
          <>
            

            


            

            



            
            
            
            <Tooltip classes={{ tooltip: classes.customTooltip }}
              title={collapsed ? i18n.t("mainDrawer.listItems.tools") || "Herramientas" : ""}
              placement="right"
            >
              <ListItem
                dense
                button
                onClick={() => setOpenToolsSubmenu(!openToolsSubmenu)}
                onMouseEnter={() => setToolsHover(true)}
                onMouseLeave={() => setToolsHover(false)}
                className={`${classes.listItem} ${classes.parentMenuItem}`}
                style={{
                  justifyContent: collapsed ? "center" : "flex-start",
                  paddingLeft: 0,
                  paddingRight: 16
                }}
              >
                <ListItemIcon style={{ minWidth: 40, marginRight: collapsed ? 0 : 16, justifyContent: "center" }}>
                  <Avatar className={`${classes.iconHoverActive} ${openToolsSubmenu || toolsHover ? "active" : ""}`}>
                    <BuildIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText style={{ display: collapsed ? "none" : "block" }}
                  primary={
                    <Typography className={classes.listItemText}>
                      {i18n.t("mainDrawer.listItems.tools") || "Herramientas"}
                    </Typography>
                  }
                />
                {!collapsed && (openToolsSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </ListItem>
            </Tooltip>
            <Collapse
              in={openToolsSubmenu}
              timeout="auto"
              unmountOnExit
              className={classes.submenuContainer}
            >
              <List component="div" disablePadding>
      {hasHelps && (
        <ListItemLink
          to="/helps"
          primary={i18n.t("mainDrawer.listItems.helps")}
          icon={<HelpOutlineIcon />}
          tooltip={collapsed}
        />
      )}

      {user.super ? (
        <ListItemLink
          to="/manual"
          primary={i18n.t("manual.title") || "Manual Usuario"}
          icon={<MenuBookIcon />}
          tooltip={collapsed}
        />
      ) : (
        <ListItem
          button
          className={classes.listItem}
          onClick={() => {
            // Redundant check handled by conditional rendering, but kept for safety if logic changes
            if (!user.super) toastError("Esta opción estará disponible muy pronto.");
          }}
          style={{
            justifyContent: collapsed ? "center" : "flex-start",
            paddingLeft: 0,
            paddingRight: 16
          }}
        >
          <ListItemIcon className={classes.listItemIcon} style={{
            minWidth: 40,
            marginRight: collapsed ? 0 : 16,
            justifyContent: "center"
          }}>
            <Avatar className={classes.iconHoverActive}>
              <MenuBookIcon />
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography className={classes.listItemText}>
                {i18n.t("manual.title") || "Manual Usuario"}
              </Typography>
            }
          />
        </ListItem>
      )}
              </List>
            </Collapse>
              <Tooltip classes={{ tooltip: classes.customTooltip }} title={collapsed ? i18n.t("mainDrawer.listItems.publications") : ""} placement="right">
                  <ListItem
                    button
                    onClick={() => setOpenPublishSubmenu(!openPublishSubmenu)}
                    onMouseEnter={() => setPublishHover(true)}
                    onMouseLeave={() => setPublishHover(false)}
                    className={`${classes.listItem} ${classes.parentMenuItem}`}
                    style={{
                      justifyContent: collapsed ? "center" : "flex-start",
                      paddingLeft: 0,
                      paddingRight: 16
                    }}
                  >
                    <ListItemIcon className={classes.listItemIcon} style={{ minWidth: 40, marginRight: collapsed ? 0 : 16, justifyContent: "center" }}>
                      <Avatar className={`${classes.iconHoverActive} ${openPublishSubmenu || publishHover ? "active" : ""}`}>
                        <Campaign />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText style={{ display: collapsed ? "none" : "block" }}
                      primary={
                        <Typography className={classes.listItemText}>
                          {i18n.t("mainDrawer.listItems.publications")}
                        </Typography>
                      }
                    />
                    {!collapsed && (openPublishSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                  </ListItem>
              </Tooltip>
              <Collapse
                in={openPublishSubmenu}
                timeout="auto"
                unmountOnExit
                className={classes.submenuContainer}
              >
                <List component="div" disablePadding>
                  <Can role={user.profile} perform="dashboard:view" yes={() => (
                    <ListItemLink
                      to="/whatsapp-status-scheduler"
                      primary={i18n.t("mainDrawer.listItems.publishStatus")}
                      icon={<WhatsAppIcon />}
                      tooltip={collapsed}
                    />
                  )} />
                  <Can role={user.profile} perform="dashboard:view" yes={() => (
                    <ListItemLink
                      to="/group-scheduler"
                      primary={i18n.t("mainDrawer.listItems.publishGroups")}
                      icon={<ForumIcon />}
                      tooltip={collapsed}
                    />
                  )} />
                  <Can role={user.profile} perform="dashboard:view" yes={() => (
                    <ListItemLink
                      to="/community-scheduler"
                      primary={i18n.t("mainDrawer.listItems.publishCommunities")}
                      icon={<PeopleIcon />}
                      tooltip={collapsed}
                    />
                  )} />
                </List>
              </Collapse>

            {user?.company?.discountPercentage > 0 && (
              <ListItemLink
                to="/reseller-panel"
                primary={i18n.t("userModal.tabs.reseller")}
                icon={<AccountBalanceWalletIcon />}
                tooltip={collapsed}
              />
            )}
          </>
        )}
      />

          {/* ADMINISTRACION COLLAPSE */}
          <Tooltip
            classes={{ tooltip: classes.customTooltip }}
            title={collapsed ? i18n.t("mainDrawer.listItems.administration") : ""}
            placement="right"
          >
            <ListItem
              button
              onClick={() => setOpenAdminSubmenu((prev) => !prev)}
              onMouseEnter={() => setAdminHover(true)}
              onMouseLeave={() => setAdminHover(false)}
              className={`${classes.listItem} ${classes.parentMenuItem}`}
              style={{
                justifyContent: collapsed ? "center" : "flex-start",
                paddingLeft: 0,
                paddingRight: 16
              }}
            >
              <ListItemIcon style={{
                minWidth: 40,
                marginRight: collapsed ? 0 : 16,
                justifyContent: "center"
              }}>
                <Avatar
                  className={`${classes.iconHoverActive} ${openAdminSubmenu || adminHover
                    ? "active"
                    : ""
                    }`}
                >
                  <SettingsIcon />
                </Avatar>
              </ListItemIcon>
              <ListItemText style={{ display: collapsed ? "none" : "block" }}
                primary={
                  <Typography className={classes.listItemText}>
                    {i18n.t("mainDrawer.listItems.administration")}
                  </Typography>
                }
                style={collapsed ? { display: "none" } : {}}
              />
              {collapsed ? null : openAdminSubmenu ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </ListItem>
          </Tooltip>

          <Collapse
            in={openAdminSubmenu}
            timeout="auto"
            unmountOnExit
            className={classes.submenuContainer}
          >
            <List dense component="div" disablePadding>
{(user.super || user.profile === "admin") && (
              <>
                <ListItemLink
                  to="/announcements"
                  primary={i18n.t("mainDrawer.listItems.annoucements")}
                  icon={<AnnouncementIcon />}
                  tooltip={collapsed}
                />
              </>
            )}
<Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/queues"
                  primary={i18n.t("mainDrawer.listItems.queues")}
                  icon={<AccountTreeOutlinedIcon />}
                  tooltip={collapsed}
                />
              )}
            />

{(user.super || user.profile === "admin") && (
                <ListItemLink
                  to="/users"
                  primary={i18n.t("mainDrawer.listItems.users")}
                  icon={<PeopleAltOutlinedIcon />}
                  tooltip={collapsed}
                />
              )}
<Can
              role={
                user.profile === "user" && user.allowConnections === "enabled"
                  ? "admin"
                  : user.profile
              }
              perform={"drawer-admin-items:view"}
              yes={() => (
                <React.Fragment>
                  <ListItemLink
                    to="/connections"
                    primary={i18n.t("mainDrawer.listItems.connections")}
                    icon={<SyncAltIcon />}
                    showBadge={connectionWarning}
                    tooltip={collapsed}
                  />
                </React.Fragment>
              )}
            />
{user.super && (
              <ListItemLink
                to="/allConnections"
                primary={i18n.t("mainDrawer.listItems.allConnections")}
                icon={<PhonelinkSetup />}
                tooltip={collapsed}
              />
            )}
{user.super && (
              <ListItemLink
                to="/admin/resellers"
                primary={i18n.t("mainDrawer.listItems.resellers")}
                icon={<AccountBalanceWalletIcon />}
                tooltip={collapsed}
              />
            )}
<Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/financeiro"
                  primary={i18n.t("mainDrawer.listItems.financeiro")}
                  icon={<LocalAtmIcon />}
                  tooltip={collapsed}
                />
              )}
            />
<Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/settings"
                  primary={i18n.t("mainDrawer.listItems.settings")}
                  icon={<SettingsOutlinedIcon />}
                  tooltip={collapsed}
                />
              )}
            />
            {/* [ANTIGRAVITY] Unificado: Empresas ahora se gestiona exclusivamente en /settings?tab=companies */}
            </List>
          </Collapse>
          {/* END ADMINISTRACION COLLAPSE */}


          {user.super && (
            <ListItemLink
              to="/resources"
              primary={i18n.t("mainDrawer.listItems.resources")}
              icon={<SpeedIcon />}
              tooltip={collapsed}
            />
          )}

          <Tooltip
            classes={{ tooltip: classes.customTooltip }}
            title={i18n.t("mainDrawer.listItems.updates") || "Actualizaciones"}
            placement="right"
          >
            <ListItem
              dense
              button
              className={`${classes.listItem} ${changesLogOpen ? classes.activeItem : ""}`}
              onClick={() => setChangesLogOpen(true)}
              style={{
                justifyContent: collapsed ? "center" : "flex-start",
                paddingLeft: 0,
                paddingRight: 16
              }}
            >
              <ListItemIcon className={classes.listItemIcon} style={{
                minWidth: 40,
                marginRight: collapsed ? 0 : 16,
                justifyContent: "center"
              }}>
                <Avatar className={`${classes.iconHoverActive} ${changesLogOpen ? "active" : ""}`}>
                  <SystemUpdateAltIcon />
                </Avatar>
              </ListItemIcon>
              <ListItemText style={{ display: collapsed ? "none" : "block" }}
                primary={
                  <Typography className={classes.listItemText}>
                    {i18n.t("mainDrawer.listItems.updates") || "Updates"}
                  </Typography>
                }
                style={collapsed ? { display: "none" } : {}}
              />
            </ListItem>
          </Tooltip>

          <ChangesLog
            open={changesLogOpen}
            onClose={() => setChangesLogOpen(false)}
          />

      {!collapsed && (
        <React.Fragment>
          <Divider />
          <Typography
            className={classes.versionContainer}
          >
            {`Botmix Ver: ${changes?.[0]?.version || "7.0.2"}`}
          </Typography>
        </React.Fragment>
      )}
    </div>
  );
};

export default MainListItems;
